import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { countryNameToCode } from './utils/countryUtils';

const UserContext = createContext();

export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  // Initialize from localStorage or default to 'IN'
  const storedCountry = localStorage.getItem('country') || 'IN';
  // Normalize country (handles both codes and full names)
  const normalizedCountry = countryNameToCode(storedCountry);
  const [country, setCountry] = useState(normalizedCountry);
  const lastCountryRef = useRef(normalizedCountry);

  // Update localStorage when country changes (always store as CODE)
  useEffect(() => {
    if (country && country !== lastCountryRef.current) {
      localStorage.setItem('country', country);
      lastCountryRef.current = country;
    }
  }, [country]);

  // Listen for localStorage changes (e.g., from other tabs)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'country' && e.newValue) {
        const normalizedCountry = countryNameToCode(e.newValue);
        if (normalizedCountry !== country) {
          setCountry(normalizedCountry);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [country]);

  // Improved sync with localStorage updates from same tab (fixes country flag disappearing after login)
  useEffect(() => {
    const checkForUpdates = () => {
      const storedCountry = localStorage.getItem('country');
      if (storedCountry) {
        const normalizedCountry = countryNameToCode(storedCountry);
        if (normalizedCountry !== country) {
          console.log('UserContext: Updating country from', country, 'to', normalizedCountry);
          setCountry(normalizedCountry);
        }
      }
    };
    
    // Check when user focuses on the app (like after login)
    window.addEventListener('focus', checkForUpdates);
    
    // Also check when the page becomes visible
    document.addEventListener('visibilitychange', checkForUpdates);
    
    // Check immediately on mount to catch any updates
    checkForUpdates();
    
    // Add interval check for immediate updates (e.g., after login)
    // This ensures UserContext updates even if focus/visibility events don't fire
    const intervalId = setInterval(checkForUpdates, 500); // Check every 500ms
    
    return () => {
      window.removeEventListener('focus', checkForUpdates);
      document.removeEventListener('visibilitychange', checkForUpdates);
      clearInterval(intervalId);
    };
  }, [country]);

  // Custom setCountry that normalizes input
  const setCountryNormalized = (value) => {
    const normalized = countryNameToCode(value);
    setCountry(normalized);
  };

  return (
    <UserContext.Provider value={{ country, setCountry: setCountryNormalized }}>
      {children}
    </UserContext.Provider>
  );
}; 